// @flow

import React, { PureComponent, Fragment } from 'react';
import type { ElementRef, Node } from 'react';
import { change, initialize } from 'redux-form';
import { Grid, Row, Col } from 'Common/Grid';
import Card from 'Common/Card';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { Bling as GPT } from 'react-gpt';
import classNames from 'classnames';
import styles from 'rootContainer/Results.scss';
import { RESET_POSTCODE } from '../actions/actionTypes';
import SortBy from 'Results/SortBy';

import BreadCrumb from 'Common/BreadCrumb';

import FilterForm from './FilterForm';
import Icon from 'Common/Icon';
import BrowseAll from 'Common/BrowseAll';
import SEO from 'Common/Seo';
import ErrorBox from 'Common/ErrorBox';
import Pagination from './Pagination';
import getFilterBreakdownProp from 'Common/getFilterBreakdownProp';
import buildUrlFromForm from 'Common/buildUrlFromForm';
import buildQuery from './buildQuery';
import { distanceOrTown } from 'Common/distanceOrTown';
import { getKey } from 'Common/formValues';
import {
  slots,
  createAdUnitPath,
  targetingResults,
  adUnitPageNames
} from 'Common/ads';
import metadata from 'App/variables/seo';
import scrollToTopOfPage from 'Common/scrollToTopOfPage';
import {
  gtmResults,
  gtmDisplayResults,
  gtmResultClick,
  gtmPageview
} from 'Common/gtmPayloads';
import { gtmEventPush } from 'utils/gtm/events';
import isWindow from 'Common/isWindow';
import isWindowProp from 'Common/isWindowProp';
import chunk from 'Common/chunk';
import * as Actions from 'actionTypes';
import Accordion from 'Common/Accordion';
import slugify from 'slugify';
import * as R from 'ramda';
import 'url-search-params-polyfill';
import PartnershipLogo from 'Common/PartnershipLogo';
import { getDerivativeDisplayName } from 'utils/derivative';
import parsedPrice from '../Common/parsedPrice';
import getResizedImage from 'utils/getResizedImage';
import { setAutocarHeaderVisibility } from 'utils/autocar';
import ActiveFilters from './ActiveFilters';

const isUrlMatch = (oldUrl, newUrl) =>
  oldUrl.pathname === newUrl.pathname && oldUrl.search === newUrl.search;

const getSelectedMake = formValues => {
  if (formValues) {
    return formValues.make ? getKey(formValues.make) : '';
  } else {
    return '';
  }
};

const createActiveFields = R.compose(
  R.keys,
  R.filter(R.identity),
  R.omit(['lat', 'long', 'nearlynew', 'used', 'isPostCodeValid', 'sortBy']),
  R.ifElse(R.prop('isPostCodeValid'), R.identity, R.dissoc('withinDistance'))
);

/**
 * <Results /> component.
 * @param {string} postcode - postcode
 * @return {class} new results instance
 */
export class Results extends PureComponent<*, *> {
  currentBounce: Function;
  debounce: Function;
  filterButtonRef: ?HTMLElement;
  fromUrl: boolean;
  initialValues: ?Object;
  interval: any;
  portalRef: any;

  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      isResultsMode: true,
      isAnimationReady: false,
      breadCrumbUrl: props.match.url,
      stickyFilterButton: false,
      showErrorMessage: false, // postcode error message
      highlight: false // highlight filterform postcode field
    };
    this.portalRef = React.createRef();
  }

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object): Object {
    /**
     * pluckFromState
     * @param {string} prop - prop
     * @param {string} key - key
     * @return {array} - options
     */
    const pluckFromState = (prop, key) =>
      getFilterBreakdownProp(state, prop, key);

    const additonalOptions = {
      fuelTypes: pluckFromState('fuelType'),
      category: pluckFromState('category'),
      colours: pluckFromState('colour'),
      doors: pluckFromState('doors'),
      seats: pluckFromState('no_of_seats'),
      transmissions: pluckFromState('transmission'),
      mileages: pluckFromState('mileage'),
      annualTax: pluckFromState('annualTax'),
      fuelConsumption: pluckFromState('ec_combined_mpg'),
      co2Emissions: pluckFromState('co2_gkm'),
      acceleration: pluckFromState('0_to_62_mph_secs'),
      insuranceGroup: pluckFromState('insuranceGroup'),
      yearFrom: pluckFromState('yearFrom'),
      yearTo: pluckFromState('yearTo'),
      engineSizeFrom: pluckFromState('badge_engine_ccFrom'),
      engineSizeTo: pluckFromState('badge_engine_ccTo')
    };

    return {
      results: state.results.results,
      redirectUrl: state.results.redirectUrl,
      pagination: state.results.pagination,
      loc: state.results.location,
      // !state.results.results -- isLoading is true when the results.results is not there (to make sure results loaded is from the deals & not postcode api)
      // !(state.results.pagination && state.results.pagination.pageSize) -- isLoading is true when the pagination.pageSize=0 (to make sure it is not just getting the results from homepage on initial search from homepage)
      isResultsLoading:
        state.results.isLoading ||
        !state.results.results ||
        !(state.results.pagination && state.results.pagination.pageSize),
      filterForm: state.form.search,
      initialValues: state.results.initialForm,
      makes: getFilterBreakdownProp(state, 'make'),
      models: getFilterBreakdownProp(state, 'model'),
      trims: getFilterBreakdownProp(state, 'trim'),
      priceFrom: getFilterBreakdownProp(state, 'cashPriceStat', 'min'),
      priceTo: getFilterBreakdownProp(state, 'cashPriceStat', 'max'),
      ui: state.ui,
      additonalOptions,
      isMobile: state.viewport.isXs,
      isError: state.ui.error,
      viewport: state.viewport.viewport
    };
  }

  pushGtm: (item: Object) => void = item => {
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push(gtmResultClick(item));
    }
  };

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillMount it fetches the content.
   * @return {void}
   */
  UNSAFE_componentWillMount() {
    const query = buildQuery(
      this.props.match.params.make,
      this.props.match.params.model,
      this.props.ui,
      this.props.location.search,
      this.props.match.params.id,
      this.props.match.url
    );

    this.fromUrl = true;
    this.initialValues = {};

    this.props.dispatch({
      type: Actions.FETCH_INITIAL_RESULTS,
      query,
      url:
        isWindow() && isWindowProp('location')
          ? window.location.pathname + window.location.search
          : ''
    });
    if (this.props.ui) {
      this.postCodeCheck(this.props.ui.lastPostCode);
    }
  }

  pushPv: () => void = () => {
    if (
      isWindow() &&
      isWindowProp('dataLayer') &&
      isWindowProp('location') &&
      this.props.results
    ) {
      window.dataLayer.push(
        gtmPageview(
          'Results',
          global.window.location.pathname,
          'Classifieds Search Results Page',
          this.props.pagination.total
        )
      );
      window.dataLayer.push(gtmDisplayResults(this.props.results));
      clearInterval(this.interval);
    }
  };

  componentWillUnmount() {
    clearInterval(this.interval);
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push({ page: '' });
    }
    window.removeEventListener('scroll', this.handleStickyFilterButtonOnScroll);
  }

  componentDidMount() {
    scrollToTopOfPage();
    GPT.enableSingleRequest();
    const adModel = this.props.match.params.model || '';
    targetingResults(this.props.match.params.make, adModel);
    this.interval = setInterval(this.pushPv, 300);

    window.addEventListener(
      'scroll',
      throttle(this.handleStickyFilterButtonOnScroll, 200)
    );
  }

  /**
   * Handles the sticky Filter button on scroll on mobile
   * @param  {SyntheticInputEvent} event the onChange event for the select
   * @returns {void} void
   */
  handleStickyFilterButtonOnScroll: () => void | boolean = () => {
    if (!this.props.isMobile) {
      return false;
    }

    if (window.scrollY > 111) {
      this.setState({ stickyFilterButton: true });
    } else {
      this.setState({ stickyFilterButton: false });
    }
  };

  /**
   * setRef
   * @param el
   */
  setRef: (el: null | ElementRef<string>) => void = el => {
    this.filterButtonRef = el;
  };

  /* eslint-disable camelcase */
  UNSAFE_componentWillReceiveProps(nextProps: Object) {
    if (
      isWindow() &&
      isWindowProp('dataLayer') &&
      isWindowProp('location') &&
      this.props.results &&
      nextProps.results &&
      nextProps.results !== this.props.results &&
      R.difference(nextProps.results, this.props.results).length !== 0 &&
      nextProps.filterForm &&
      nextProps.filterForm.values
    ) {
      const hasDealer =
        global.window.location.pathname.split('/')[2] === 'dealer';
      const dealerName = hasDealer ? nextProps.results[0].dealer.name : '';
      window.dataLayer.push(
        gtmPageview(
          'Results',
          global.window.location.pathname,
          'Classifieds Search Results Page'
        )
      );
      window.dataLayer.push(
        gtmResults(
          nextProps.filterForm.values,
          dealerName,
          nextProps.pagination.total
        )
      );
      window.dataLayer.push(gtmDisplayResults(nextProps.results));
    }

    if (
      nextProps.redirectUrl &&
      nextProps.redirectUrl !== nextProps.match.url &&
      !window.location.search
    ) {
      this.props.history.push(nextProps.redirectUrl);
    }

    const initialFormChanged =
      nextProps &&
      this.props &&
      nextProps.initialValues &&
      this.props.initialValues &&
      !R.equals(nextProps.initialValues, this.props.initialValues);

    if (
      (!this.props.initialValues && nextProps.initialValues) ||
      initialFormChanged
    ) {
      this.props.dispatch(initialize('search', nextProps.initialValues));
    }

    if (!isUrlMatch(nextProps.location, this.props.location)) {
      const query = buildQuery(
        nextProps.match.params.make,
        nextProps.match.params.model,
        nextProps.ui,
        nextProps.location.search,
        this.props.match.params.id,
        this.props.match.url
      );
      this.fromUrl = true;
      this.initialValues = {};
      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query,
        url:
          isWindow() && isWindowProp('location')
            ? window.location.pathname + window.location.search
            : ''
      });
    }
    if (isWindow() && isWindowProp('location')) {
      this.setState({
        breadCrumbUrl: window.location.pathname
      });
    }

    // Hack to fix weird animation issue
    setTimeout(
      () =>
        this.setState({
          isAnimationReady: true
        }),
      3000
    );
  }

  fetchUpdatedQuery: () => void = () => {
    if (isWindow() && isWindowProp('location')) {
      const pathname = global.window.location.pathname.split('/');
      const hasDealer = pathname[2] === 'dealer';
      const isAll = pathname[2] !== 'all';
      const hasMake = !hasDealer && isAll;

      const query = buildQuery(
        hasMake ? pathname[2] : null,
        hasMake ? pathname[3] : null,
        this.props.ui,
        global.window.location.search,
        hasDealer ? pathname[3] : null,
        this.props.match.url
      );

      const url = window.location.pathname + window.location.search;
      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query,
        url
      });
    }
  };

  getResults: () => void = () => {
    if (!this.props.initialValues || !this.props.filterForm.values) {
      return;
    }
    const postcode = this.props.filterForm.values.postCode;
    this.postCodeCheck(postcode);
    const urlParams = new URLSearchParams(this.props.location.search);

    const url = buildUrlFromForm(this.props.filterForm, urlParams);

    if (isWindow() && isWindowProp('history')) {
      history.pushState({}, '', url);
      this.fetchUpdatedQuery();
    } else {
      this.props.history.push(url);
    }
  };

  getMakeAndModelText: () => string = () => {
    if (this.props.filterForm && this.props.filterForm.values) {
      const formValues = this.props.filterForm.values;
      const make = formValues.make ? getKey(formValues.make) : '';
      const model = formValues.model ? getKey(formValues.model) : '';
      return `${make} ${model}`;
    } else {
      return 'cars';
    }
  };

  postCodeCheck: (postcode: String) => void = postcode => {
    this.props.dispatch({
      type: Actions.CHECK_POSTCODE,
      postcode
    });
  };

  debouncedFunc: () => any = () => {
    if (!this.debounce) {
      this.debounce = debounce(this.postCodeCheck, 1000);
    }
    return this.debounce;
  };

  checkPostcode: () => void = value => {
    let postcode;
    if (value) {
      if (!value.target.value) {
        this.resetField('postCode');
        return;
      } else {
        postcode = value.target.value;
      }
    } else {
      postcode =
        this.props.filterForm.values && this.props.filterForm.values.postCode
          ? this.props.filterForm.values.postCode
          : null;
    }
    this.currentBounce = this.debouncedFunc();
    this.currentBounce(postcode);
  };

  isPostcodeValid: () => void = () => {
    return !!(
      this.props.ui &&
      this.props.ui.postCode &&
      this.props.ui.postCode.isValid
    );
  };

  toggleMode: () => void = () => {
    const isResultsMode = this.state.isResultsMode;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: isResultsMode ? 'Go to search filters' : 'Return to results'
      }
    });

    if (
      this.props.isMobile &&
      typeof window !== 'undefined' &&
      window.document &&
      document.body
    ) {
      document.body.style.overflow = isResultsMode ? 'hidden' : 'auto';
      if (isResultsMode) {
        setAutocarHeaderVisibility(false);
      } else {
        setAutocarHeaderVisibility(true);
      }
    }

    this.setState(
      {
        isResultsMode: !isResultsMode
      },
      () => (isResultsMode ? scrollToTopOfPage() : null)
    );
  };

  renderPartnerLogo: () => Node = () => {
    return <PartnershipLogo extendStyle={styles.logo} />;
  };

  /**
   * resetField.
   * @param {string} field - field
   *
   * @return {void}
   */
  resetField: (field: string) => void = field => {
    if (field === 'nearlynew' || field === 'used') {
      this.props.dispatch(change('search', field, true));
    } else {
      this.props.dispatch(change('search', field, null));
    }

    if (field === 'nearlyNewDeals') {
      const url = new URL(window.location);
      url.searchParams.delete('maxPlate');
      url.searchParams.delete('minPlate');
      this.props.dispatch(change('search', 'yearFrom', null));
      this.props.dispatch(change('search', 'mileage', null));
      this.props.history.push(url);
    }

    if (field === 'make') {
      this.props.dispatch(change('search', 'model', null));
      this.props.dispatch(change('search', 'trim', null));
    }

    if (field === 'model') {
      this.props.dispatch(change('search', 'trim', null));
    }

    if (field === 'postCode') {
      this.props.dispatch({ type: RESET_POSTCODE });
      this.props.dispatch(change('search', 'withinDistance', null));
      if (
        this.props.filterForm.values &&
        this.props.filterForm.values.sortBy &&
        getKey(this.props.filterForm.values.sortBy) === 'distance'
      ) {
        this.props.dispatch(change('search', 'sortBy', null));
      }
    }
  };

  removeActiveFilter: (field: string) => void = key => {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Remove Active Filter'
      }
    });
    this.resetField(key);
  };

  resetAllFields(fields: any) {
    const urlParams = new URLSearchParams(this.props.location.search);
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Clear all'
      }
    });
    R.keys(fields).forEach(field => {
      this.resetField(field);
    });

    if (urlParams.get('minPlate') || urlParams.get('maxPlate')) {
      this.resetField('nearlyNewDeals');
    }
  }

  fadeOutAfter: (delay: number) => void = delay => {
    setTimeout(
      () =>
        this.setState({
          showErrorMessage: false
        }),
      delay
    );
  };

  displayPostcodeErrorMessage = (showErrorMessage: boolean) => {
    if (showErrorMessage) {
      this.setState({ showErrorMessage: true, highlight: true });
      this.fadeOutAfter(3000);
    } else {
      this.setState({ showErrorMessage: false });
    }
  };

  /**
   * Renders the about page.
   *
   * @return {JSX} about html.
   */
  render(): Node {
    const {
      results,
      isResultsLoading,
      filterForm,
      makes,
      models,
      pagination,
      priceFrom,
      priceTo,
      additonalOptions,
      initialValues,
      isMobile,
      isError,
      viewport
    } = this.props;

    const { classResultsFound, mtl, mtm, mts } = styles;

    const resultsLength = pagination ? pagination.total : 0;

    let formValues = initialValues || {};
    let adsSeoProps = {
      make: getKey(formValues.make),
      model: getKey(formValues.model),
      category: getKey(formValues.category),
      page: adUnitPageNames['results']
    };
    let resultsWithAds = [];
    let meta = {};
    let dealerId = getKey(formValues.dealerId);

    if (filterForm && filterForm.values) {
      formValues = filterForm.values;
      adsSeoProps = {
        make: getKey(filterForm.values.make),
        model: getKey(filterForm.values.model),
        category: getKey(filterForm.values.category),
        page: adUnitPageNames['results']
      };
      dealerId = getKey(filterForm.values.dealerId);
    }

    const selectedMake = getSelectedMake(formValues);
    const activeFilters = createActiveFields(formValues);

    const count =
      pagination && pagination.total
        ? pagination.total.toLocaleString('en')
        : 0;

    let dealerName;

    if (results && results.length > 0 && dealerId) {
      dealerName = results[0].dealer.name;
    }

    if (dealerId && results && results.length > 1) {
      adsSeoProps = { dealer: dealerName };
      meta = metadata.resultsDealer(adsSeoProps.dealer, dealerId, pagination);
    } else if (adsSeoProps.category && !adsSeoProps.make) {
      meta = metadata.resultsBodyStyle(adsSeoProps.category, count, pagination);
    } else {
      meta = metadata.resultsCar(adsSeoProps, count, formValues, pagination);
    }

    const urlParams = new URLSearchParams(this.props.location.search);

    let nearlyNewDeals = false;
    if (urlParams.get('minPlate') || urlParams.get('maxPlate')) {
      nearlyNewDeals = true;
    }

    let mappedResults = results
      ? results.map((item, index) => (
          <Card
            id={item.sourceId}
            name={getDerivativeDisplayName(item.derivative)}
            make={item.derivative.make}
            range={item.derivative.range}
            trim={item.derivative.trim}
            price={parsedPrice(item.totalPrice)}
            year={item.year}
            distanceOrTown={distanceOrTown(item, formValues.isPostcodeValid)}
            mileage={item.mileage}
            fuelType={item.derivative.fuelType}
            doors={item.derivative.doors}
            transmission={item.derivative.transmission}
            engineSize={item.technical && item.technical.badgeEngineSizeLitres}
            enginePower={item.technical && item.technical.engine_power_bhp}
            image={
              (item.images &&
                getResizedImage(
                  item.images[0],
                  viewport,
                  'horizontalCardImage'
                )) ||
              null
            }
            carousel={item.images}
            dealerImage={item.dealer.logo}
            key={index}
            history={this.props.history}
            item={item}
            handleClick={this.pushGtm}
            itemPos={index}
            gcLid={urlParams.get('gclid')}
            network={urlParams.get('network')}
            gtmSectionName={'autoTraderClickThrough'}
            url={item.detailsUrl}
            isMobile={isMobile}
            priceIndicatorRating={item.priceIndicatorRating}
            attentionGrabber={item.attentionGrabber}
            portalRef={this.portalRef}
          />
        ))
      : null;

    if (mappedResults) {
      let chunkedResults = chunk(mappedResults, 5);

      // TODO: We're commenting these slots out until we re-work mobile ads.

      // if (isMobile) {
      //   const chunkedOptions = ['first', 'last'];
      //   chunkedResults.forEach((_, i) =>
      //     _.push(
      //       <div className={styles.resultsAd} key={`ResultsAd${i}`}>
      //         <GPT
      //           id={slots[`result_${chunkedOptions[i]}`].id}
      //           adUnitPath={createAdUnitPath(adsSeoProps)}
      //           collapseEmptyDiv={true}
      //           renderWhenViewable={false}
      //           sizeMapping={slots[`result_${chunkedOptions[i]}`].sizeMapping}
      //           targeting={slots[`result_${chunkedOptions[i]}`].targeting}
      //         />
      //       </div>
      //     )
      //   );
      // }

      chunkedResults.forEach(
        (_, i) => (resultsWithAds = resultsWithAds.concat(_))
      );

      if (resultsWithAds) {
        let itemListElement = [];
        resultsWithAds.map((item, index) => {
          let make = item.props.make || '';
          let range = item.props.range || '';
          let name = item.props.name || '';
          let id = item.props.id || '';
          let price = item.props.price || '';

          itemListElement.push({
            '@type': 'ListItem',
            position: index,
            item: {
              '@type': 'Product',
              name: `${make} ${range} ${name}`,
              // $FlowFixMe
              url: `${process.env.BASE_URL}/used-cars/${slugify(
                make
              )}/${slugify(range)}/${slugify(name)}/${id}`,
              offers: {
                '@type': 'Offer',
                price: price,
                priceCurrency: 'GBP'
              }
            }
          });
        });
        meta.schema = {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: itemListElement
          })
        };
      }
    }

    const postcodeVisited =
      filterForm &&
      filterForm.fields &&
      filterForm.fields.postCode &&
      filterForm.fields.postCode.visited;

    const stickyButtonStyle = this.state.stickyFilterButton
      ? classNames(styles.toggleModeBtnContainer, styles.stickyButton)
      : styles.toggleModeBtnContainer;

    return (
      <Fragment>
        <div
          ref={this.portalRef}
          className={styles.portal}
          data-test="portal"
        ></div>
        {!this.state.isResultsMode && (
          <div
            className={styles.filterFormBackdrop}
            onClick={this.toggleMode}
          ></div>
        )}

        <Grid className={styles.results}>
          <SEO {...meta} />
          {isError && <ErrorBox type={500} />}
          {!isError && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <div className={styles.header}>
                  <BreadCrumb breadCrumbUrl={this.state.breadCrumbUrl} />
                  <div className={stickyButtonStyle} ref={this.setRef}>
                    <button
                      className={styles.toggleModeBtn}
                      onClick={this.toggleMode}
                    >
                      <div className={styles.title}>
                        <Icon
                          iconName={'chevron-down-small'}
                          size={'default'}
                          className={styles.icon}
                        />
                        <span>Filters</span>
                      </div>
                      <span className={styles.numbers}>
                        (
                        {nearlyNewDeals
                          ? activeFilters.length + 1
                          : activeFilters.length}
                        ) applied
                      </span>
                    </button>
                    <SortBy
                      sortIcon={true}
                      fetchUpdatedQuery={this.fetchUpdatedQuery}
                      selectedValue={formValues.sortBy}
                      isPostcode={formValues.postCode}
                      displayPostcodeErrorMessage={
                        this.displayPostcodeErrorMessage
                      }
                      showErrorMessage={this.state.showErrorMessage}
                    />
                  </div>
                </div>
                <div className={styles.titleContainer}>
                  <h1 className={mtl}>{meta.title}</h1>
                  {this.renderPartnerLogo()}
                </div>
                <div className={styles.headerAd}>
                  <GPT
                    id={slots.header.id}
                    adUnitPath={createAdUnitPath(adsSeoProps)}
                    collapseEmptyDiv={true}
                    renderWhenViewable={false}
                    sizeMapping={slots.header.sizeMapping}
                    targeting={slots.header.targeting}
                  />
                </div>
                <div className={styles.introContainer}>
                  {!isMobile && (
                    <p className={styles.introText}>{meta.blurb} </p>
                  )}
                  {this.renderPartnerLogo()}
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row className={mtm}>
              <Col colXs={12} colSm={12} colMd={4} colLg={4}>
                <h3 className={classNames(classResultsFound, styles.results)}>
                  Cars found{' '}
                  <span>{count ? count.toLocaleString('en') : 0}</span>
                </h3>
              </Col>
            </Row>
          )}
          {!isError && !isMobile && (
            <Row className={mtm}>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <ActiveFilters
                  fields={formValues}
                  dealerName={dealerName}
                  activeFilters={activeFilters}
                  removeActiveFilter={this.removeActiveFilter}
                  isMobile={isMobile}
                  resetAllFields={() => this.resetAllFields(formValues)}
                />
              </Col>
            </Row>
          )}
          {!isError && ( // Is mobile
            <Row className={classNames(styles.borderTop, mts)}>
              <Col
                colXs={12}
                colSm={12}
                colMd={4}
                colLg={3}
                className={classNames(
                  styles.filterForm,
                  this.state.isResultsMode && styles.isHidden
                )}
              >
                <div>
                  <div className={mtl}>
                    <FilterForm
                      onSubmit={() => {}}
                      getResults={this.getResults}
                      initialValues={initialValues}
                      activeFilters={activeFilters}
                      fields={formValues}
                      makes={makes}
                      models={models}
                      priceFrom={priceFrom}
                      priceTo={priceTo}
                      checkPostcode={this.checkPostcode}
                      isPostcodeValid={this.isPostcodeValid()}
                      additonalOptions={additonalOptions}
                      dealerName={dealerName}
                      fetchUpdatedQuery={this.fetchUpdatedQuery}
                      resultsLength={resultsLength}
                      isMobile={isMobile}
                      removeActiveFilter={this.removeActiveFilter}
                      resetAllFields={() => this.resetAllFields(formValues)}
                      resetField={this.resetField}
                      isListingsPage={true}
                      closeFilters={this.toggleMode}
                      postcodeVisited={postcodeVisited || this.state.highlight}
                    />
                  </div>
                  <div className={classNames([styles.sideAd, mtl])}>
                    {!isMobile && (
                      <GPT
                        id={slots.sidenav.id}
                        adUnitPath={createAdUnitPath(adsSeoProps)}
                        collapseEmptyDiv={true}
                        renderWhenViewable={false}
                        sizeMapping={slots.sidenav.sizeMapping}
                        targeting={slots.sidenav.targeting}
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col
                colXs={12}
                colSm={12}
                colMd={8}
                colLg={9}
                className={classNames(
                  styles.resultsGroup,
                  this.state.isResultsMode
                    ? styles.resultsGroupFadeIn
                    : styles.resultsGroupFadeOut
                )}
              >
                {this.state.isAnimationReady && isResultsLoading && (
                  <div className={styles.spinner}>
                    <div className={styles.doubleBounce1} />
                    <div className={styles.doubleBounce2} />
                  </div>
                )}
                <div
                  className={classNames(
                    styles.cards,
                    isResultsLoading ? styles.cardsLoading : null
                  )}
                >
                  {resultsWithAds.length < 1 && !isResultsLoading && (
                    <ErrorBox type={'noResults'} />
                  )}
                  {resultsWithAds}
                  {resultsWithAds.length >= 1 && pagination && (
                    <Pagination
                      fetchUpdatedQuery={this.fetchUpdatedQuery}
                      currentPage={pagination.page}
                      totalPages={Math.ceil(
                        pagination.total / pagination.pageSize
                      )}
                    />
                  )}
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row className={mtl}>
              <Col colXs={12}>
                <Accordion
                  accordionHeight={175}
                  gtmSectionName={'accordionBrowse'}
                >
                  <BrowseAll
                    items={selectedMake ? models : makes}
                    text="models"
                    make={selectedMake}
                    history={this.props.history}
                  />
                </Accordion>
              </Col>
            </Row>
          )}
        </Grid>
      </Fragment>
    );
  }
}

// $FlowFixMe
export default connect(Results.mapStateToProps)(Results);



// WEBPACK FOOTER //
// ./src/Results/index.js